import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth, useUser } from "@clerk/clerk-react";

import { SidebarTab } from "../../types";
import { dbStorage } from "@/utils/dbStorage";
import { filesystemService } from "@/utils/filesystem";
import { projectService } from "@/services/ProjectService";
import { pyodideService } from "@/services/PyodideService";

const useProjectManagement = (updateLogs: (log: string) => void, setActiveTab: (tab: SidebarTab) => void) => {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSavingToCloud, setIsSavingToCloud] = useState(false);
  const { getToken } = useAuth();

  useEffect(() => {
    if (!loading) return;
    const tid = setTimeout(() => {
      updateLogs("Loading timeout reached. Please try again if needed.");
      setLoading(false);
    }, 15_000);
    return () => clearTimeout(tid);
  }, [loading, updateLogs]);

  const fetchAndCacheProjects = useCallback(
    async (forceRefresh = false) => {
      try {
        const token = await getToken();
        if (!token) throw new Error("No auth token");
        projectService.setToken(token);
        const orgId = projectService.getCurrentOrganizationId();
        const needsRefresh =
          forceRefresh || (await dbStorage.shouldRefreshProjects(orgId));
        if (!needsRefresh) {
          window.dispatchEvent(new CustomEvent("projectsRefreshed"));
          return;
        }
        updateLogs("Fetching project list...");
        const projects = await projectService.listProjects();
        await dbStorage.storeProjects(projects, orgId);
        updateLogs("Project list cached successfully");
        setTimeout(
          () => window.dispatchEvent(new CustomEvent("projectsRefreshed")),
          100
        );
        return projects;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        updateLogs(`Error caching projects: ${msg}`);
        console.error(e);
      }
    },
    [getToken, updateLogs]
  );

  const createNewProject = useCallback(async () => {
    setLoading(true);
    updateLogs("Creating new project...");
    try {
      if (pyodideService.isReady()) {
        updateLogs("Clearing previous filesystem contents...");
        await pyodideService.clearFilesystem();
      }
      const token = await getToken();
      if (!token) throw new Error("No auth token");
      projectService.setToken(token);
      const project = await projectService.createProject(undefined, 'blank');
      setProjectId(project.id);
      setProjectName(project.name);

      const url = new URL(window.location.href);
      url.searchParams.set("project", project.id);
      window.history.replaceState({}, "", url.toString());

      updateLogs("Creating default project files...");
      await pyodideService.createNewProject();
      await filesystemService.saveToIndexedDB();
      updateLogs("New project created: " + project.id);

      await fetchAndCacheProjects();
      return project.name;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      updateLogs(`Error creating project: ${msg}`);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getToken, updateLogs, fetchAndCacheProjects]);

  const loadProject = useCallback(
    async (id: string) => {
      if (id === projectId) return projectName!;
      setLoading(true);
      updateLogs(`Loading project: ${id}`);
      const MAX_RETRIES = 2;
      let attempts = 0;
      while (attempts <= MAX_RETRIES) {
        try {
          if (pyodideService.isReady()) {
            await pyodideService.clearFilesystem();
          }
          const token = await getToken();
          if (!token) throw new Error("No auth token");
          projectService.setToken(token);
          const proj = await projectService.getProject(id);
          setProjectId(id);
          setProjectName(proj.name);
          setActiveTab(SidebarTab.Editor);

          // Try to fetch files from cloud storage first
          await fetchProjectFilesFromCloud(id);

          const ok = await filesystemService.loadFromIndexedDB(id);
          if (!ok) await pyodideService.createNewProject();
          filesystemService.triggerAutoSave();
          updateLogs("Project loaded successfully");
          const url = new URL(window.location.href);
          url.searchParams.set("project", id);
          window.history.replaceState({}, "", url.toString());
          return proj.name;
        } catch (e) {
          attempts++;
          const msg = e instanceof Error ? e.message : String(e);
          const isNetErr =
            /ERR_INSUFFICIENT_RESOURCES|Failed to fetch|NetworkError/.test(
              msg
            );
          if (isNetErr && attempts <= MAX_RETRIES) {
            const wait = Math.min(1000 * 2 ** (attempts - 1), 5000);
            updateLogs(
              `Network error, retrying in ${wait / 1000}s (${attempts}/${MAX_RETRIES})`
            );
            await new Promise((r) => setTimeout(r, wait));
            continue;
          }
          updateLogs(`Error loading project: ${msg}`);
          console.error(e);
          throw e;
        } finally {
          setLoading(false);
        }
      }
    },
    [getToken, projectId, projectName, updateLogs, setActiveTab]
  );

  const fetchProjectFilesFromCloud = useCallback(async (projectId: string) => {
    try {
      const token = await getToken();
      if (!token) {
        updateLogs("Warning: No auth token available for fetching cloud files");
        return false;
      }

      projectService.setToken(token);

      const userId = projectService.getCurrentUserId();
      const organizationId = projectService.getCurrentOrganizationId();

      updateLogs("Checking for existing files in cloud storage...");

      const apiBaseUrl = import.meta.env.VITE_BACKEND_URL || "";
      const response = await fetch(`${apiBaseUrl}/api/projects/${projectId}/files`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': userId || '',
          'X-Organization-ID': organizationId || ''
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          updateLogs("No files found in cloud storage, using local files");
          return false;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        updateLogs("No files found in cloud storage, using local files");
        return false;
      }

      const filesCount = result.data?.files_count || 0;
      if (filesCount === 0) {
        updateLogs("No files found in cloud storage, using local files");
        return false;
      }

      updateLogs(`Found ${filesCount} files in cloud storage, downloading...`);

      // Now download the actual file contents
      const downloadResponse = await fetch(`${apiBaseUrl}/api/projects/${projectId}/download-files`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': userId || '',
          'X-Organization-ID': organizationId || ''
        }
      });

      if (!downloadResponse.ok) {
        throw new Error(`Download failed: HTTP ${downloadResponse.status}: ${downloadResponse.statusText}`);
      }

      const downloadResult = await downloadResponse.json();

      if (!downloadResult.success) {
        updateLogs("Failed to download files from cloud storage");
        return false;
      }

      const downloadedFiles = downloadResult.data?.files || {};
      const downloadedCount = Object.keys(downloadedFiles).length;

      if (downloadedCount === 0) {
        updateLogs("No files downloaded from cloud storage");
        return false;
      }

      updateLogs(`Downloaded ${downloadedCount} files, updating local storage...`);

      // Update IndexedDB with the downloaded files
      const { set } = await import('idb-keyval');
      const snapshotKey = `fs-snapshot:${projectId}`;

      // Store the files in the same format as filesystemService expects
      await set(snapshotKey, downloadedFiles);

      updateLogs(`Successfully updated local storage with ${downloadedCount} files from cloud`);

      return true;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      updateLogs(`Warning: Could not fetch files from cloud: ${errorMessage}`);
      console.warn("Error fetching project files from cloud:", error);
      return false;
    }
  }, [getToken, updateLogs]);

  const saveProjectToCloud = useCallback(async () => {
    const currentProjectId = projectService.getCurrentProjectId();
    if (!currentProjectId) {
      updateLogs("Error: No project loaded to save to cloud");
      return;
    }

    setIsSavingToCloud(true);
    updateLogs("Preparing to save project files to cloud storage...");

    try {
      const token = await getToken();
      if (!token) throw new Error("No auth token");

      projectService.setToken(token);

      let filesystemSnapshot: Record<string, { type: string; content: string }> | null = null;

      updateLogs("Collecting project files from filesystem...");

      if (pyodideService.isReady()) {
        try {
          filesystemSnapshot = await pyodideService.getFilesystemSnapshot();
          if (filesystemSnapshot && Object.keys(filesystemSnapshot).length > 0) {
            updateLogs(`Got ${Object.keys(filesystemSnapshot).length} files from Pyodide worker`);
          }
        } catch (error) {
          updateLogs('Failed to get snapshot from worker mode, trying alternative methods...');
        }
      }

      if (!filesystemSnapshot) {
        const key = `fs-snapshot:${currentProjectId}`;
        try {
          const { get } = await import('idb-keyval');
          const indexedDBSnapshot = await get(key);
          if (indexedDBSnapshot && Object.keys(indexedDBSnapshot).length > 0) {
            filesystemSnapshot = indexedDBSnapshot;
            updateLogs(`Got ${Object.keys(indexedDBSnapshot).length} files from IndexedDB snapshot`);
          }
        } catch (error) {
          updateLogs('Failed to get snapshot from IndexedDB, building manual snapshot...');
        }
      }

      if (!filesystemSnapshot) {
        filesystemSnapshot = {};

        const isBinaryFile = (filePath: string): boolean => {
          const extension = filePath.split('.').pop()?.toLowerCase() || '';
          const binaryExtensions = [
            'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'ico', 'svg',
            'pdf', 'zip', 'tar', 'gz', 'mp3', 'mp4', 'wav', 'ogg',
            'exe', 'dll', 'so', 'bin', 'dat'
          ];
          return binaryExtensions.includes(extension);
        };

        if (filesystemService.pyodide || pyodideService.isReady()) {
          try {
            const fileList = await filesystemService.listPyodideFiles("/project");
            updateLogs(`Found ${fileList.length} items in project directory, processing files...`);

            const processFileItem = async (item: any): Promise<void> => {
              if (item.type === 'file') {
                try {
                  const content = await filesystemService.readFileFromPyodide(item.path);
                  if (content !== null) {
                    let relativePath = item.path;
                    if (relativePath.startsWith('/project/')) {
                      relativePath = relativePath.substring('/project/'.length);
                    }
                    if (relativePath.startsWith('/')) {
                      relativePath = relativePath.substring(1);
                    }

                    const isBinary = isBinaryFile(item.path);
                    if (isBinary && typeof content === 'string') {
                      filesystemSnapshot![relativePath] = { type: "binary", content: content };
                    } else if (content instanceof ArrayBuffer || content instanceof Uint8Array) {
                      const base64Content = btoa(String.fromCharCode(...new Uint8Array(content)));
                      filesystemSnapshot![relativePath] = { type: "binary", content: base64Content };
                    } else if (typeof content === 'string') {
                      filesystemSnapshot![relativePath] = { type: "text", content: content };
                    }
                  }
                } catch (fileError) {
                  updateLogs(`Warning: Could not read file ${item.path}: ${fileError}`);
                }
              } else if (item.type === 'folder' && item.contents) {
                for (const subItem of item.contents) {
                  await processFileItem(subItem);
                }
              }
            };

            for (const item of fileList) {
              await processFileItem(item);
            }

            if (Object.keys(filesystemSnapshot).length > 0) {
              updateLogs(`Built manual snapshot with ${Object.keys(filesystemSnapshot).length} files`);
            }
          } catch (listError) {
            updateLogs(`Failed to list files from pyodide: ${listError}`);
          }
        }

        if (Object.keys(filesystemSnapshot).length === 0 && filesystemService.pendingFiles) {
          for (const [path, content] of Object.entries(filesystemService.pendingFiles)) {
            let relativePath = path;
            if (relativePath.startsWith('/project/')) {
              relativePath = relativePath.substring('/project/'.length);
            }
            if (relativePath.startsWith('/')) {
              relativePath = relativePath.substring(1);
            }

            if (typeof content === 'string') {
              filesystemSnapshot![relativePath] = { type: "text", content: content };
            } else if (content instanceof ArrayBuffer || content instanceof Uint8Array) {
              const base64 = btoa(String.fromCharCode(...new Uint8Array(content)));
              filesystemSnapshot![relativePath] = { type: "binary", content: base64 };
            }
          }
          if (Object.keys(filesystemSnapshot).length > 0) {
            updateLogs(`Used pending files to create snapshot with ${Object.keys(filesystemSnapshot).length} files`);
          }
        }
      }

      if (!filesystemSnapshot || Object.keys(filesystemSnapshot).length === 0) {
        throw new Error("Could not collect project files. Please ensure your project has files to save.");
      }

      const fileCount = Object.keys(filesystemSnapshot).length;
      updateLogs(`Successfully collected ${fileCount} files, uploading to cloud storage...`);

      const apiBaseUrl = import.meta.env.VITE_BACKEND_URL || "";

      const userId = projectService.getCurrentUserId();
      const organizationId = projectService.getCurrentOrganizationId();

      updateLogs(`Sending request with User ID: ${userId}, Organization ID: ${organizationId}`);

      const response = await fetch(`${apiBaseUrl}/api/projects/${currentProjectId}/save-files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-User-ID': userId || '',
          'X-Organization-ID': organizationId || ''
        },
        body: JSON.stringify({
          files: filesystemSnapshot
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          if (errorText) {
            errorMessage = errorText;
          }
        }

        throw new Error(`Failed to save files to cloud: ${errorMessage}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Unknown error occurred while saving files');
      }

      const savedCount = result.data?.saved_count || fileCount;
      const filesUrl = result.data?.files_url;

      updateLogs(`Successfully saved ${savedCount} files to Google Cloud Storage!`);

      if (filesUrl) {
        updateLogs(`View files at: ${filesUrl}`);
      }

      updateLogs(`Files saved at: ${result.data?.saved_at || new Date().toISOString()}`);

      return {
        success: true,
        savedCount,
        filesUrl,
        savedAt: result.data?.saved_at
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      updateLogs(`Error saving project to cloud: ${errorMessage}`);
      console.error("Error saving project to cloud:", error);

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setIsSavingToCloud(false);
    }
  }, [getToken, updateLogs]);

  const returnToProjects = useCallback(async () => {
    if (pyodideService.isReady()) {
      updateLogs("Clearing filesystem contents...");
      try {
        await pyodideService.clearFilesystem();
        updateLogs("Shutting down Pyodide Web Worker...");
        await pyodideService.shutdown();
      } catch (e) {
        updateLogs(`Error during Pyodide cleanup: ${e}`);
        console.error(e);
      }
    }
    setProjectId(null);
    setProjectName(null);
    projectService.setCurrentProjectId(null);

    const url = new URL(window.location.href);
    url.searchParams.delete("project");
    window.history.replaceState({}, "", url.toString());
    updateLogs("Returned to projects screen");
  }, [updateLogs]);

  return {
    projectId,
    projectName,
    loading,
    isSavingToCloud,
    createNewProject,
    loadProject,
    returnToProjects,
    fetchAndCacheProjects,
    saveProjectToCloud,
    fetchProjectFilesFromCloud,
  };
};


export default useProjectManagement;